"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Subscription, Vehicle } from "@/types";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import {
  generateTimeSlots,
  toBackendDiscount,
  toUiAdjustment,
} from "@/lib/utils";
import { PriceAdjustmentInput } from "@/components/common/PriceAdjustmentInput";
import { paymentService } from "@/services/paymentService";
import type { PaymentOutput } from "@/types/payment.types";
import type { PaymentMode } from "@/types/enums.types";
import { toast } from "sonner";

const weekDays = [
  { label: "Monday", value: "MONDAY" },
  { label: "Tuesday", value: "TUESDAY" },
  { label: "Wednesday", value: "WEDNESDAY" },
  { label: "Thursday", value: "THURSDAY" },
  { label: "Friday", value: "FRIDAY" },
  { label: "Saturday", value: "SATURDAY" },
  { label: "Sunday", value: "SUNDAY" },
];

// Generate time slots with 15-minute intervals
const timeSlots = generateTimeSlots();

// 8-wash plans that have 2 scheduling rules
const TWO_RULE_PLANS = ["PLATINUM", "GOLD", "SILVER LITE"];

interface EditSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: Subscription | null;
  vehicles: Vehicle[];
  onRefreshPaginated?: () => Promise<void>;
}

export function EditSubscriptionDialog({
  open,
  onOpenChange,
  subscription,
  vehicles,
  onRefreshPaginated,
}: EditSubscriptionDialogProps) {
  const { updateUserSubscriptionDetails, refreshSubscriptions } =
    useSubscriptions();

  const [formData, setFormData] = useState({
    day: "",
    time: "",
    day2: "",
    time2: "",
    vehicleId1: "",
    vehicleId2: "",
    paymentMode: "CASH" as PaymentMode,
    isActive: false,
  });

  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [initialIsActive, setInitialIsActive] = useState(false);
  const [hasSecondRule, setHasSecondRule] = useState(false);

  // Price adjustment lives on the subscription's Payment record, not on the
  // UserSubscription itself. Held UI-signed (+ = surcharge).
  const [priceAdjustment, setPriceAdjustment] = useState(0);
  const [initialPriceAdjustment, setInitialPriceAdjustment] = useState(0);
  const [payment, setPayment] = useState<PaymentOutput | null>(null);

  const isPaid = payment?.status === "COMPLETED";

  // Check if this subscription's plan requires 2 rules
  const isTwoRulePlan = useMemo(() => {
    if (!subscription) return false;
    const planName =
      subscription.planName?.replace(/\s+/g, " ").trim().toUpperCase() || "";
    return TWO_RULE_PLANS.includes(planName);
  }, [subscription]);

  // Filter vehicles by customer
  const customerVehicles = useMemo(() => {
    if (!subscription) return [];
    return vehicles.filter((v) => v.customerId === subscription.customerId);
  }, [vehicles, subscription]);

  // Load subscription data when dialog opens
  useEffect(() => {
    if (open && subscription) {
      const loadSubscriptionData = async () => {
        try {
          setLoadingDetails(true);
          // Fetch full subscription details to get rule IDs
          const fullSubscription =
            await userSubscriptionService.getUserSubscriptionById(
              subscription.id,
            );

          const firstRule =
            fullSubscription.rules && fullSubscription.rules.length > 0
              ? fullSubscription.rules[0]
              : null;
          const secondRule =
            fullSubscription.rules && fullSubscription.rules.length > 1
              ? fullSubscription.rules[1]
              : null;

          setHasSecondRule(!!secondRule);

          setFormData({
            day:
              firstRule?.day?.toUpperCase() ||
              subscription.recurringDay.toUpperCase() ||
              "MONDAY",
            time: firstRule?.time || subscription.preferredTime || "10:00 AM",
            day2:
              secondRule?.day?.toUpperCase() ||
              subscription.recurringDay2?.toUpperCase() ||
              "WEDNESDAY",
            time2:
              secondRule?.time || subscription.preferredTime2 || "10:00 AM",
            vehicleId1:
              firstRule?.vehicleId ||
              subscription.vehicleId ||
              customerVehicles[0]?.id ||
              "",
            vehicleId2:
              secondRule?.vehicleId ||
              subscription.vehicleId ||
              customerVehicles[0]?.id ||
              "",
            paymentMode: "CASH" as PaymentMode,
            isActive: fullSubscription.isActive,
          });
          setInitialIsActive(fullSubscription.isActive);

          // Load the linked payment so the adjustment can be shown and edited.
          const linkedPayment = await paymentService.getPaymentByRefId(
            subscription.id,
          );
          setPayment(linkedPayment);
          setFormData((prev) => ({
            ...prev,
            paymentMode: linkedPayment?.mode || "CASH",
          }));
          const uiAdjustment = toUiAdjustment(linkedPayment?.discount ?? 0);
          setPriceAdjustment(uiAdjustment);
          setInitialPriceAdjustment(uiAdjustment);
        } catch (error) {
          console.error("Error loading subscription data:", error);
          // Fallback to subscription props
          setFormData({
            day: subscription.recurringDay.toUpperCase() || "MONDAY",
            time: subscription.preferredTime || "10:00 AM",
            day2: subscription.recurringDay2?.toUpperCase() || "WEDNESDAY",
            time2: subscription.preferredTime2 || "10:00 AM",
            vehicleId1: subscription.vehicleId || customerVehicles[0]?.id || "",
            vehicleId2: subscription.vehicleId || customerVehicles[0]?.id || "",
            paymentMode: "CASH" as PaymentMode,
            isActive: subscription.isActive ?? false,
          });
          setInitialIsActive(subscription.isActive ?? false);
        } finally {
          setLoadingDetails(false);
        }
      };

      loadSubscriptionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscription]);

  const handleSaveDetails = async () => {
    if (!subscription) return;

    // Validate
    if (!formData.day || !formData.time) {
      return;
    }
    if ((isTwoRulePlan || hasSecondRule) && (!formData.day2 || !formData.time2)) {
      return;
    }

    try {
      setLoading(true);
      await updateUserSubscriptionDetails(subscription.id, {
        day: formData.day,
        time: formData.time,
        vehicleId: formData.vehicleId1 || undefined,
        ...(isTwoRulePlan || hasSecondRule
          ? {
              day2: formData.day2,
              time2: formData.time2,
              vehicleId2: formData.vehicleId2 || undefined,
            }
          : {}),
      });

      // Persist the price adjustment only when it actually changed, so a
      // schedule-only edit never touches the invoice.
      if (payment && priceAdjustment !== initialPriceAdjustment) {
        await paymentService.updateDiscount(
          payment.id,
          toBackendDiscount(priceAdjustment),
        );
        setInitialPriceAdjustment(priceAdjustment);
      }

      if (payment && formData.paymentMode !== payment.mode) {
        await paymentService.updatePaymentMode(payment.id, formData.paymentMode);
      }

      if (onRefreshPaginated) {
        await onRefreshPaginated();
      }
      onOpenChange(false);
    } catch (error: any) {
      // Schedule errors are handled in context; surface adjustment failures here.
      const message = error?.response?.data?.message;
      if (message) {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIsActive = async (newIsActive: boolean) => {
    if (!subscription) return;

    let statusUpdated = false;

    try {
      setLoading(true);
      if (newIsActive) {
        await userSubscriptionService.reactivateUserSubscription(subscription.id);
      } else {
        await userSubscriptionService.expireUserSubscription(subscription.id);
      }
      statusUpdated = true;

      setFormData((prev) => ({
        ...prev,
        isActive: newIsActive,
      }));
      setInitialIsActive(newIsActive);
      await refreshSubscriptions();
      if (onRefreshPaginated) {
        await onRefreshPaginated();
      }
      onOpenChange(false);
    } catch (error) {
      // Revert toggle only if the status update request failed.
      if (!statusUpdated) {
        setFormData((prev) => ({ ...prev, isActive: initialIsActive }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!subscription) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Subscription</DialogTitle>
          <DialogDescription>
            Update subscription details and status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Active Status Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="isActive" className="text-base">
                Subscription Status
              </Label>
              <p className="text-sm text-muted-foreground">
                {formData.isActive
                  ? "Subscription is active"
                  : "Subscription is expired"}
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={handleToggleIsActive}
              disabled={loading}
            />
          </div>

          {/* Schedule 1 */}
          <div className="rounded-lg border p-4 space-y-4">
            <div className="text-sm font-medium text-muted-foreground">
              {isTwoRulePlan || hasSecondRule ? "Schedule 1" : "Schedule"}
            </div>

            {/* Schedule 1 - Day Selection */}
            <div className="grid gap-2">
              <Label htmlFor="day">Day *</Label>
              <Select
                value={formData.day}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, day: value }))
                }
                disabled={loading || loadingDetails}
              >
                <SelectTrigger id="day">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {weekDays.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Schedule 1 - Time Selection */}
            <div className="grid gap-2">
              <Label htmlFor="time">Time *</Label>
              <Select
                value={formData.time}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, time: value }))
                }
                disabled={loading || loadingDetails}
              >
                <SelectTrigger id="time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot.value} value={slot.label}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vehicle for Schedule 1 */}
            <div className="grid gap-2">
              <Label htmlFor="vehicleId1">Vehicle</Label>
              {customerVehicles.length === 0 ? (
                <div className="text-sm text-muted-foreground p-2 border rounded-md">
                  No vehicles available for this customer
                </div>
              ) : (
                <Select
                  value={formData.vehicleId1}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, vehicleId1: value }))
                  }
                  disabled={loading || loadingDetails}
                >
                  <SelectTrigger id="vehicleId1">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerVehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Schedule 2 — Only shown for 2-rule plans or if subscription already has 2 rules */}
          {(isTwoRulePlan || hasSecondRule) && (
            <div className="rounded-lg border p-4 space-y-4">
              <div className="text-sm font-medium text-muted-foreground">
                Schedule 2
              </div>

              <div className="grid gap-2">
                <Label htmlFor="day2">Day *</Label>
                <Select
                  value={formData.day2}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, day2: value }))
                  }
                  disabled={loading || loadingDetails}
                >
                  <SelectTrigger id="day2">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    {weekDays.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="time2">Time *</Label>
                <Select
                  value={formData.time2}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, time2: value }))
                  }
                  disabled={loading || loadingDetails}
                >
                  <SelectTrigger id="time2">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot.value} value={slot.label}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle for Schedule 2 */}
              <div className="grid gap-2">
                <Label htmlFor="vehicleId2">Vehicle</Label>
                {customerVehicles.length === 0 ? (
                  <div className="text-sm text-muted-foreground p-2 border rounded-md">
                    No vehicles available for this customer
                  </div>
                ) : (
                  <Select
                    value={formData.vehicleId2}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, vehicleId2: value }))
                    }
                    disabled={loading || loadingDetails}
                  >
                    <SelectTrigger id="vehicleId2">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerVehicles.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          {/* Payment Mode */}
          <div className="grid gap-2">
            <Label htmlFor="paymentMode">Payment Mode</Label>
            <Select
              value={formData.paymentMode}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentMode: value as PaymentMode,
                }))
              }
              disabled={loading || loadingDetails || !payment || isPaid}
            >
              <SelectTrigger id="paymentMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">Cash</SelectItem>
                <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                <SelectItem value="DEBIT_CARD">Debit Card</SelectItem>
                <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                <SelectItem value="POS">POS</SelectItem>
                <SelectItem value="APPLE_PAY">Apple Pay</SelectItem>
                <SelectItem value="G_PAY">G-Pay</SelectItem>
                <SelectItem value="WALLET">Wallet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Price Adjustment */}
          <div className="border-t pt-4">
            <PriceAdjustmentInput
              value={priceAdjustment}
              onChange={setPriceAdjustment}
              disabled={loading || loadingDetails || !payment || isPaid}
              disabledReason={
                isPaid
                  ? "This invoice is fully paid. Reopen it before changing the price."
                  : !payment && !loadingDetails
                    ? "No invoice is linked to this subscription."
                    : undefined
              }
              basePrice={payment?.totalAmount}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDetails}
            disabled={
              loading || loadingDetails || !formData.day || !formData.time
            }
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
